import React, { useCallback, useContext, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import AddAgentModal from "./AddAgentsModal";
import AgentCards from "./AgentCards";
import { Col, Row } from "react-bootstrap";
import MaleIcon from "../../../assets/icons/MaleIcon.svg";
import FemaleIcon from "../../../assets/icons/FemaleIcon.svg";
import AuthContext from "../../../auth/AuthContext";
import AgentService from "../../../services/AgentService";
import AlertService from "../../../services/AlertService";
import UserService from "../../../services/UserService";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Agents = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const loading = loadingUsers || loadingAgents;
    const agentAccess = authContext.getPermissions("agents");  
    if (agentAccess.filter((f) => f.access.includes(1)).length === 0) {
        navigate("/");
    }

const loadUsers = useCallback(async (signal) => {
    setLoadingUsers(true);
    try {
      const [data] = await Promise.all([
        UserService.GetList(authContext, signal),
        delay(2000)
      ]);

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setUsers(data.data.map((u) => ({ label: u.label, value: u.value })));
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoadingUsers(false); 
    }
  }, [authContext]);

const loadAgents = useCallback(async (signal) => { 
    setLoadingAgents(true);
    try {
      const [data] = await Promise.all([
        AgentService.GetAll(authContext, signal), 
        delay(2000)
      ]);

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setAgents(
          data.data.map((agent) => ({
            id: agent.id,
            name: agent.agentName,
            icon: agent.gender === "F" ? FemaleIcon : MaleIcon,
            status: agent.isActive ? "Active" : "Inactive",
            data: agent,
          }))
        );
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoadingAgents(false);
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadUsers(controller.signal);
    loadAgents(controller.signal);
    return () => controller.abort(); 
  }, [loadUsers, loadAgents]);

  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedAgent(null);
    setShowModal(true);
  };

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary py-2" onClick={handleAddClick}>
          <FaPlus className="pb-1 me-1" />
          Add New Agent
        </button>
      </div>

      <Row className="d-flex justify-content-start gy-3">
        {loading
          ? Array.from({ length: 8 }).map((_, idx) => (
            <Col key={idx} xs={6} sm={4} md={3} lg={3}>
              <center className="p-2">
                <Skeleton shape="circle" size="4rem" className="mb-3" />
                <Skeleton width="60%" style={{ marginTop: "1rem" }} className="mb-3" />
                <Skeleton width="40%" style={{ marginTop: "0.5rem" }} className="mb-3" />
                <Skeleton width="35%" style={{ marginTop: "0.5rem" }} className="mb-3" />
              </center>
            </Col>
          ))
          : agents.map((card, idx) => (
            <Col key={idx} xs={6} sm={4} md={3} lg={3}>
              <AgentCards
                name={card.name}
                icon={card.icon}
                status={card.status}
                onEdit={() => {
                  setIsEditMode(true);
                  setSelectedAgent(card.data);
                  setShowModal(true);
                }}
              />
            </Col>
          ))}
      </Row>

      <AddAgentModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        isEditMode={isEditMode}
        agentData={selectedAgent}
        users={users}
        reloadAgents={loadAgents}
      />
    </div>
  );
};

export default Agents;
